import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * PublicBid — landlord-facing page for reviewing and signing an AI-drafted
 * repair bid. No auth. Loaded via /public/bid/:token.
 *
 * Flow:
 *   1. Fetch bid + Before photos via GET /api/public/bid/:token
 *   2. Show scope, materials table, labor breakdown, total
 *   3. Landlord types full name → types signature (must exactly match) →
 *      checks approval box → POST /api/public/bid/:token/sign
 *   4. On success, WO flips to quote_status="approved" server-side; page
 *      shows a "Signed on {date}" confirmation with a "You're all set" CTA.
 */

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function PublicBid() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sig, setSig] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signedResult, setSignedResult] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/bid/${token}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `Load failed (${r.status})`);
      setData(j);
    } catch (e) {
      setErr(e.message || "Couldn't load this bid");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!name.trim()) return setErr("Please type your full legal name");
    if (sig.trim().toLowerCase() !== name.trim().toLowerCase()) return setErr("Signature must match the name above exactly");
    if (!accepted) return setErr("Check the approval box to continue");
    setErr(null);
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/public/bid/${token}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signer_name: name.trim(),
          signer_email: email.trim(),
          signature_text: sig.trim(),
          accepted: true,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `Sign failed (${r.status})`);
      setSignedResult(j);
      await load();
    } catch (e) {
      setErr(e.message || "Sign failed");
    } finally {
      setBusy(false);
    }
  };

  if (err && !data) {
    return (
      <ErrorScreen title="Can't open this bid" detail={err} />
    );
  }
  if (!data) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white/60 font-black uppercase tracking-widest text-sm">Loading…</div>;

  const b = data.bid || {};
  const already = b.status === "signed" || signedResult?.ok;
  const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-slate-50 text-[#0F1F3A]" data-testid="public-bid-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Repair estimate from</div>
            <div className="text-xl font-black" data-testid="public-bid-company">{data.company}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">WO {data.work_order_number || "—"}</div>
            <div className="text-xs font-bold text-slate-700 truncate max-w-[220px]">{data.address}{data.unit ? ` · Apt ${data.unit}` : ""}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6 space-y-5">
        {already && (
          <div className="rounded-xl bg-emerald-500 text-white p-4 shadow-lg" data-testid="public-bid-signed-banner">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Signed &amp; approved</div>
            <div className="text-lg font-black mt-1">
              Approved by {signedResult?.signer_name || b.signer_name} on {new Date(b.signed_at || signedResult?.signed_at).toLocaleString()}
            </div>
            <div className="text-xs mt-1.5 opacity-90">You're all set — the crew has been notified and will schedule the work.</div>
          </div>
        )}

        {/* Scope */}
        <section className="rounded-2xl bg-white p-5 shadow" data-testid="public-bid-scope">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">Scope of work</div>
          <div className="text-[15px] leading-relaxed">{b.scope || "(no scope)"}</div>
        </section>

        {/* Photos */}
        {data.photos?.length > 0 && (
          <section className="rounded-2xl bg-white p-5 shadow" data-testid="public-bid-photos">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">What the crew found on-site</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {data.photos.map((p, i) => (
                <a key={i} href={`${API_BASE}${p.url}`} target="_blank" rel="noreferrer"
                  data-testid={`public-bid-photo-${i}`}
                  className="block bg-slate-100 rounded-lg overflow-hidden aspect-[4/3]">
                  <img src={`${API_BASE}${p.url}`} alt={p.caption || ""} className="w-full h-full object-cover"/>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Materials + labor */}
        <section className="rounded-2xl bg-white p-5 shadow overflow-hidden" data-testid="public-bid-lineitems">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Line items</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-2">Material</th>
                <th className="py-2 text-center w-12">Qty</th>
                <th className="py-2 text-right w-20">Unit</th>
                <th className="py-2 text-right w-24">Total</th>
              </tr>
            </thead>
            <tbody>
              {(b.materials || []).map((m, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0" data-testid={`public-bid-material-${i}`}>
                  <td className="py-2">{m.name}</td>
                  <td className="py-2 text-center">{m.qty} {m.unit || ""}</td>
                  <td className="py-2 text-right">{money(m.unit_cost)}</td>
                  <td className="py-2 text-right font-semibold">{money(m.total)}</td>
                </tr>
              ))}
              {(b.materials || []).length === 0 && (
                <tr><td colSpan={4} className="py-3 text-center text-slate-400 italic text-xs">No materials — labor only</td></tr>
              )}
            </tbody>
          </table>

          <div className="mt-4 pt-4 border-t border-slate-200 text-sm space-y-1.5">
            <Row2 l="Materials" r={money(b.subtotal_materials)} />
            <Row2 l={`Labor · ${b.labor_hours}h @ ${money(b.labor_rate)}/h`} r={money(b.subtotal_labor)} />
            {Number(b.tax || 0) > 0 && <Row2 l={`Tax (${(Number(b.tax_rate) * 100).toFixed(2)}%)`} r={money(b.tax)} />}
            <div className="flex justify-between pt-2 border-t border-slate-200 text-lg font-black" data-testid="public-bid-total">
              <span>Total</span>
              <span>{money(b.total)}</span>
            </div>
          </div>
          {b.notes && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Notes</div>
              <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{b.notes}</div>
            </div>
          )}
        </section>

        {/* Sign block */}
        {!already && (
          <form onSubmit={submit} className="rounded-2xl bg-white p-5 shadow-lg border-2 border-[#00C4CC]" data-testid="public-bid-sign-form">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC] mb-3">Approve &amp; sign</div>
            <p className="text-sm text-slate-700 mb-4 leading-relaxed">
              To approve this estimate, type your full legal name and repeat it as your typed signature.
              This is a legally binding acceptance to proceed with the work at the price shown.
            </p>
            <label className="block mb-3">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Your full name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
                data-testid="public-bid-name"
                placeholder="e.g. Jane Landlord"
                className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 px-3 text-[15px] font-medium focus:outline-none focus:border-[#00C4CC]"/>
            </label>
            <label className="block mb-3">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Email (optional, for a signed copy)</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                data-testid="public-bid-email"
                placeholder="you@example.com"
                className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 px-3 text-[15px] font-medium focus:outline-none focus:border-[#00C4CC]"/>
            </label>
            <label className="block mb-3">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Type your name again as your signature</span>
              <input value={sig} onChange={(e) => setSig(e.target.value)}
                data-testid="public-bid-signature"
                placeholder="Type exactly as above"
                className={`mt-1 w-full h-14 rounded-lg border-2 px-3 text-2xl italic font-signature focus:outline-none focus:border-[#00C4CC] ${
                  sig && sig.trim().toLowerCase() === name.trim().toLowerCase() ? "border-emerald-500" : "border-slate-200"
                }`}
                style={{ fontFamily: "'Great Vibes', 'Segoe Script', 'Brush Script MT', cursive" }}/>
            </label>
            <label className="flex items-start gap-2.5 mb-4 cursor-pointer text-sm">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
                data-testid="public-bid-accept"
                className="mt-1 h-4 w-4 accent-[#00C4CC] flex-shrink-0"/>
              <span className="text-slate-700 leading-snug">
                I authorize <b>{data.company}</b> to perform the work above for <b>{money(b.total)}</b>. I understand additional charges may apply if the scope changes after the crew starts.
              </span>
            </label>
            {err && <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium" data-testid="public-bid-err">{err}</div>}
            <button type="submit" disabled={busy}
              data-testid="public-bid-sign"
              className="w-full h-12 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-sm">
              {busy ? "Signing…" : "Sign & Approve →"}
            </button>
            <div className="text-[10px] text-slate-500 mt-3 text-center">
              Signed audit records your IP address and timestamp. Valid for {b.valid_days} days · Expires {b.expires_at ? new Date(b.expires_at).toLocaleDateString() : "n/a"}
            </div>
          </form>
        )}

        <footer className="text-center text-[10px] text-slate-400 font-black uppercase tracking-widest pt-2 pb-6">
          Powered by Field Radar
        </footer>
      </main>
    </div>
  );
}

function Row2({ l, r }) {
  return (
    <div className="flex justify-between text-[13px]">
      <span className="text-slate-600">{l}</span>
      <span className="font-semibold">{r}</span>
    </div>
  );
}

function ErrorScreen({ title, detail }) {
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-6">
      <div className="max-w-md text-center" data-testid="public-bid-error">
        <div className="text-6xl mb-3">📭</div>
        <h1 className="text-2xl font-black text-[#0F1F3A]">{title}</h1>
        <p className="text-slate-600 mt-2 text-sm">{detail}</p>
      </div>
    </div>
  );
}
